import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import {
  Observable,
  catchError,
  retry,
  throwError,
  timer,
} from 'rxjs';

import { ToastService } from '../ui/toast/toast.service';

/**
 * Intercepteur HTTP global :
 * - Injecte automatiquement le token Bearer (cookie access_token)
 * - Retry unique différé sur erreurs réseau / 5xx pour requêtes idempotentes
 * - 401 → redirection /login + toast warning + purge cookies
 * - 5xx / réseau → toast erreur générique (dédupliqué)
 *
 * Anti-flood : l'edge Cloudflare rate-limite par IP. Un status 0 est très
 * souvent un 429 edge sans en-têtes CORS (le navigateur masque le code).
 * Réessayer agressivement entretient le blocage et chaque échec toasté en
 * boucle donne « Impossible de joindre le serveur » partout. Donc :
 *  - 1 seul retry, différé (2.5 s + jitter) au lieu de 2 rapprochés ;
 *  - le toast réseau est dédupliqué (max 1 / 20 s).
 *
 * 100% async, RxJS, signal-friendly.
 */
const NETWORK_TOAST_COOLDOWN_MS = 20_000;
let lastNetworkToastAt = 0;

export const vendorHttpInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const cookies = inject(CookieService);
  const router = inject(Router);
  const toast = inject(ToastService);

  // ── Auth header ──
  let modified = req;
  // API cross-origin (host vendor-api) OU chemins API SAME-ORIGIN proxifiés
  // par nginx (regle frontend-resilience : /finance, /telephonie,
  // /admin/supervision, /analytics…). Sans ça, ces appels relatifs partent
  // SANS Bearer -> 401 (bug dépenses 2026-07-08).
  const SAME_ORIGIN_API = /^\/(warehouses|stock|movements|movement-bons|batches|scan|labels|products)(\/|\?|$)|^\/(finance|telephonie|admin\/supervision|admin\/onboarding|admin\/support|admin\/prospection|me\/onboarding|me\/search|analytics|social\/ai|social\/order-links|files|migration|shipping|vendors|pos)(\/|\?|$)/;
  // /docs SPA vs API : seuls articles|search|stats sont proxifiés (cf. nginx).
  const SAME_ORIGIN_DOCS = /^\/docs\/(articles|search|stats)(\/|\?|$)/;
  const isApiCall =
    /vendor-api\.codincloud\.com/.test(req.url) ||
    /localhost:8000/.test(req.url) ||
    SAME_ORIGIN_API.test(req.url) ||
    SAME_ORIGIN_DOCS.test(req.url);
  const hasAuth = req.headers.has('Authorization');
  if (isApiCall && !hasAuth) {
    const token = cookies.get('access_token');
    if (token) {
      modified = req.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
      });
    }
  }

  const isIdempotent = ['GET', 'HEAD', 'OPTIONS'].includes(modified.method.toUpperCase());

  return next(modified).pipe(
    retry({
      count: isIdempotent ? 1 : 0,
      delay: (error: HttpErrorResponse) => {
        const retryable =
          error.status === 0 ||
          error.status === HttpStatusCode.BadGateway ||
          error.status === HttpStatusCode.ServiceUnavailable ||
          error.status === HttpStatusCode.GatewayTimeout;
        if (!retryable) throw error;
        // Délai long + jitter : laisse la fenêtre de rate-limit edge se
        // refermer au lieu de la re-déclencher immédiatement.
        const delayMs = 2500 + Math.floor(Math.random() * 1500);
        return timer(delayMs);
      },
    }),
    catchError((error: HttpErrorResponse) => {
      // 401 = session expirée → redirection /login (cas standard)
      // 403 ambigu : si le détail backend indique un problème d'auth
      // (token revoked/expired/missing), on traite comme 401 plutôt
      // que de laisser l'utilisateur bloqué silencieusement. Le vrai 403
      // (permission métier manquante) reste affiché tel quel par les pages.
      const rawError = error.error as { detail?: unknown } | string | null | undefined;
      const detail = String(
        rawError && typeof rawError === 'object' ? rawError.detail ?? '' : '',
      ).toLowerCase();
      // Jamais traiter une page HTML (Cloudflare challenge) comme échec auth :
      // le détail JSON métier seul peut matcher token|authoriz|…
      const looksLikeHtml =
        typeof rawError === 'string' && /<!doctype html|<html[\s>]/i.test(rawError);
      const isAuthFailure =
        error.status === HttpStatusCode.Unauthorized ||
        (error.status === HttpStatusCode.Forbidden &&
          !looksLikeHtml &&
          /token|authoriz|authent|session|revoked|expired|credenti/.test(detail));
      const isLoginCall =
        req.url.includes('/authentications/login') ||
        req.url.includes('/auth/login');
      if (isAuthFailure && !isLoginCall) {
        toast.warning('Votre session a expiré. Veuillez vous reconnecter.', 'Session');
        try { cookies.delete('access_token', '/'); } catch {}
        try { cookies.delete('refresh_token', '/'); } catch {}
        try { localStorage.removeItem('access_token'); } catch {}
        try { localStorage.removeItem('refresh_token'); } catch {}
        try { localStorage.removeItem('user'); } catch {}
        router.navigate(['/login'], { queryParams: { reason: 'session_expired' } });
      } else if (error.status === 0 || error.status === 429) {
        const now = Date.now();
        if (now - lastNetworkToastAt > NETWORK_TOAST_COOLDOWN_MS) {
          lastNetworkToastAt = now;
          toast.danger(
            error.status === 429
              ? 'Trop de requêtes — patientez quelques secondes.'
              : 'Connexion au serveur perturbée. Nouvelle tentative en cours…',
            'Réseau',
          );
        }
      } else if (
        error.status === HttpStatusCode.PaymentRequired &&
        (error.error as any)?.error === 'insufficient_credits'
      ) {
        // Crédits IA épuisés (contrôle total des crédits) → message clair + CTA
        // recharge. Le solde/coût viennent du handler 402 backend.
        const e = error.error as { price?: number; balance?: number };
        const parts: string[] = ['Crédits IA insuffisants pour cette génération.'];
        if (typeof e?.balance === 'number') parts.push(`Solde : ${e.balance}.`);
        if (typeof e?.price === 'number') parts.push(`Coût : ${e.price}.`);
        parts.push('Rechargez vos crédits IA pour continuer.');
        toast.warning(parts.join(' '), 'Crédits IA', 7000);
      } else if (error.status >= 500 && error.status < 600) {
        toast.danger(
          (error.error as any)?.detail || error.message || 'Erreur serveur. Réessayez.',
          `Erreur ${error.status}`,
        );
      }
      return throwError(() => error);
    }),
  );
};
